import {
  type CreateAdminWorkflowDto,
  type UpdateAdminWorkflowDto,
  type V2AdminWorkflowDto,
  type V2AdminWorkflowLookupDto,
  type V2AdminWorkflowsPageDto,
  type V2AdminWorkflowsSummaryDto,
  v2AdminWorkflowsControllerCreate,
  v2AdminWorkflowsControllerDetail,
  v2AdminWorkflowsControllerList,
  v2AdminWorkflowsControllerLookup,
  v2AdminWorkflowsControllerRemove,
  v2AdminWorkflowsControllerSummary,
  v2AdminWorkflowsControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminWorkflowLookupSchema,
  V2AdminWorkflowSchema,
  V2AdminWorkflowsPageSchema,
  V2AdminWorkflowsSummarySchema
} from '@idc-forms/schemas/api-v2/admin-workflows'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de flujos de trabajo contra la API v2 (ADR 0019/0020). Consume el
 * SDK generado con el Bearer de la sesión staff v2, pagina por cursor y usa el
 * contrato de consulta rico. Identifica todo por id público: el flujo por `wfl_`,
 * cada paso de la secuencia por `sta_` y la autoría por `usr_`.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer` con
 * bracket notation. El serializer por defecto del SDK usa `deepObject` de UN solo
 * nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type WorkflowsAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type WorkflowsAdminV2Row = V2AdminWorkflowsPageDto['data'][number]

function toListQuery(query: WorkflowsAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const workflowsAdminV2Service = {
  list: async (
    accessToken: string,
    query: WorkflowsAdminV2Query
  ): Promise<V2AdminWorkflowsPageDto> => {
    const result = await v2AdminWorkflowsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminWorkflowsPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<WorkflowsAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminWorkflowsSummaryDto> => {
    const result = await v2AdminWorkflowsControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminWorkflowsSummarySchema, result.data)
  },

  lookup: async (accessToken: string): Promise<V2AdminWorkflowLookupDto> => {
    const result = await v2AdminWorkflowsControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminWorkflowLookupSchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminWorkflowDto> => {
    const result = await v2AdminWorkflowsControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminWorkflowSchema, result.data)
  },

  create: async (
    accessToken: string,
    body: CreateAdminWorkflowDto
  ): Promise<V2AdminWorkflowDto> => {
    const result = await v2AdminWorkflowsControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminWorkflowDto
  ): Promise<V2AdminWorkflowDto> => {
    const result = await v2AdminWorkflowsControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminWorkflowsControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
